import React, { FC } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { Alert, Badge, Button, Card, CardBody, Col, Row } from 'reactstrap';
import {
  HotelAgreements,
  HotelIntegrationProviderNames,
  HotelReservation,
  HotelReservationStatus,
} from '../../../shared/hotel';
import {
  CollapseContainer,
  CollapseInfo,
  DateT,
  DateTime,
  ElementHeader,
  ExternalLinkButton,
  LinkButton,
  TermsLoader,
  TermsLoaderWrapper,
  UserStateComponent,
} from '../../components';
import { Currency } from '../../components/Currency';
import { MaterialIconText } from '../../components/MaterialIconText';
import { TranslationKey, useTranslation } from '../../translations';
import { Fetcher, useConvention, useFetcher } from '../../utils';
import { captureError } from '../../utils/errorHandling';
import { ReservationDates, ReservationStatus } from './MyReservationWidget';
import { getReservationNights } from './util';

import './MyReservation.scss';

export const MyReservation: FC = () => {
  const {
    params: { id },
  } = useRouteMatch<{ id: string }>();

  const { ts } = useTranslation();

  const reservationInfo = useFetcher(async () => {
    try {
      return await api.getHotelReservationById(id);
    } catch (error) {
      captureError(error as Error);
      throw error;
    }
  }, [id]);

  if (!reservationInfo.success) {
    return <Fetcher inline result={reservationInfo} />;
  }

  const reservation = reservationInfo.data;
  const reservationId = reservation.extReservationId ?? reservation.id;
  const hotelResId = reservation.extendedAttributes?.hotelConfirmationNumber;

  const { hotel } = reservation;

  const title = (
    <>
      {ts('reservation')} #{reservationId} {hotelResId ? `/ ${hotelResId}` : null}
      {/* eslint-disable-next-line react/jsx-no-literals */}
      {hotel ? <small> at {hotel.name}</small> : null}
    </>
  );

  return (
    <UserStateComponent>
      <div className="reservation-info">
        <ElementHeader icon={{ iconName: 'hotel' }} title={title}>
          <>
            <ReservationStatus reservation={reservation} />
            <ReservationDates reservation={reservation} />
          </>
        </ElementHeader>
        <Row>
          <Col className="mb-3" lg={6} xs={12}>
            <HotelManageLink reservation={reservation} />
            <HotelRoomInfo reservation={reservation} />
          </Col>
          <Col className="mb-3" lg={6} xs={12}>
            <TermsLoaderWrapper hideEmpty name="hotelReservationAnnouncement">
              {(terms) => (
                <Alert className="mb-3" color="warning" fade={false}>
                  {terms}
                </Alert>
              )}
            </TermsLoaderWrapper>
            <HotelGuestList reservation={reservation} />
          </Col>
          <Col xs={12}>
            <HotelPolicyAgreements reservation={reservation} />
          </Col>
        </Row>
      </div>
    </UserStateComponent>
  );
};

interface ReservationInfoProps {
  readonly reservation: HotelReservation;
}

const HotelManageLink: FC<ReservationInfoProps> = ({ reservation }) => {
  const { ts } = useTranslation();

  return (
    <Card className="mb-3">
      <CardBody className="d-flex flex-row align-content-top flex-wrap">
        <div className="p-2 flex-grow-1 align-self-top">
          <LinkButton block className="align-self-center" color="secondary" outline to="/hotel">
            {ts('back_to_hotel_reservations')}
          </LinkButton>
        </div>
        <div className="p-2 ml-2 flex-grow-1 justify-center align-self-top">
          <HotelBookingLink reservation={reservation} />
        </div>
      </CardBody>
    </Card>
  );
};

const HotelBookingLink: FC<ReservationInfoProps> = ({ reservation }) => {
  const convention = useConvention();
  const { ts } = useTranslation();

  const { bookingUrl, managedBy, status } = reservation;

  if (!bookingUrl) {
    return (
      <div className="text-secondary m-1">
        <MaterialIconText name="email" small>
          <small>
            {ts(
              'please_check_your_confirmation_email_for_instructions_on_managing_your_reservation',
            )}
          </small>
        </MaterialIconText>
      </div>
    );
  }

  if (
    [HotelReservationStatus.Cancelled, HotelReservationStatus.CancelledByEvent].includes(status)
  ) {
    return (
      <div className="text-danger m-1">
        <MaterialIconText name="remove_circle_outline" small>
          <small>{ts('this_reservation_has_been_cancelled_and_cannot_be_modified')}</small>
        </MaterialIconText>
      </div>
    );
  }

  if (!convention.hotelsOpen) {
    return (
      <>
        <Button block color="primary" disabled id="updateCancelReservation">
          {ts('update__cancel_reservation')}
        </Button>
        <div className="text-danger mt-2">
          <MaterialIconText name="lock" small>
            <small>{ts('hotel.modifications_not_allowed')}</small>
          </MaterialIconText>
        </div>
      </>
    );
  }

  return (
    <>
      <div>
        <ExternalLinkButton block color="primary" href={bookingUrl} id="updateCancelReservation">
          {ts('update__cancel_reservation')}
        </ExternalLinkButton>
      </div>
      {managedBy && (
        <div className="text-secondary mt-2">
          <MaterialIconText name="open_in_new" small>
            <small>
              {ts(
                'this_link_will_take_you_to_update_or_cancel_reservation',
                HotelIntegrationProviderNames[managedBy],
              )}
            </small>
          </MaterialIconText>
        </div>
      )}
    </>
  );
};

const HotelRoomInfo: FC<ReservationInfoProps> = ({ reservation }) => {
  const { guests = [], hotel, room, extendedAttributes, status } = reservation;
  const { ts } = useTranslation();

  if (!hotel || !room) {
    return null;
  }

  const isCancelled = [
    HotelReservationStatus.Cancelled,
    HotelReservationStatus.CancelledByEvent,
  ].includes(status);

  const breakdown = (!isCancelled && extendedAttributes?.rate?.breakdown) || {};

  const breakdownLength = Object.keys(breakdown).length;

  const additionalFees = extendedAttributes?.rate?.additionalFees ?? [];

  const rateTotal = (!isCancelled && extendedAttributes?.rate?.total) || 0;
  const reservationStay = getReservationNights({ reservation });

  return (
    <Card>
      <CardBody>
        <Row>
          {isCancelled && (
            <Col className="text-center" xs={12}>
              <Alert color="danger" fade={false}>
                <p>
                  <strong>{ts('this_reservation_has_been_cancelled')}</strong>.
                </p>
              </Alert>
            </Col>
          )}
          <Col md={12} xl={6}>
            <h4>{ts('reservation_information')}</h4>
            <hr />
            <div>
              <strong>{hotel.name}</strong>
            </div>
            <ul className="list-unstyled">
              <li>{hotel.addressLine1},</li>
              {hotel.addressLine2 && <li>{hotel.addressLine2},</li>}
              <li>
                {hotel.addressCity}, {hotel.addressState}, {hotel.addressZipcode}
              </li>
              {hotel.addressCountry && hotel.addressCountry !== 'Unknown' && (
                <li>{hotel.addressCountry}</li>
              )}
            </ul>
            <ul className="list-unstyled reservation-info-list">
              <li>
                <div>
                  <MaterialIconText name="hotel">
                    <strong>{ts('room_type')}</strong>
                  </MaterialIconText>
                </div>
                <div>{room.name}</div>
              </li>
              <li>
                <div>
                  <MaterialIconText name="person">
                    <strong>{ts('no_of_guests')}</strong>
                  </MaterialIconText>
                </div>
                <div>{guests.length}</div>
              </li>
              <hr />
              {reservationStay?.nights && (
                <li>
                  <div>
                    <MaterialIconText name="bedtime">
                      <strong>{ts('nights')}</strong>
                    </MaterialIconText>
                  </div>
                  <div>{reservationStay.nights}</div>
                </li>
              )}
              <li>
                <div>
                  <MaterialIconText name="flight_land">
                    <strong>{ts('check_in')}</strong>
                  </MaterialIconText>
                </div>
                <div>
                  <DateT
                    options={{ day: '2-digit', month: 'long', year: 'numeric' }}
                    value={reservation.checkIn!}
                  />
                </div>
              </li>
              <li>
                <div>
                  <MaterialIconText name="flight_takeoff">
                    <strong>{ts('check_out')}</strong>
                  </MaterialIconText>
                </div>
                <div>
                  <DateT
                    options={{ day: '2-digit', month: 'long', year: 'numeric' }}
                    value={reservation.checkOut!}
                  />
                </div>
              </li>
              {reservation.updatedAt && (
                <>
                  <hr />
                  <li className="text-muted">
                    <small>
                      {ts('updated_at')}: <DateTime value={reservation.updatedAt} />
                    </small>
                  </li>
                </>
              )}
            </ul>
          </Col>
          <Col className="d-xl-none" xs={12}>
            <hr />
          </Col>
          <Col md={12} xl={6}>
            <h4>{ts('rate_breakdown')}</h4>
            <hr />
            <ul className="list-unstyled reservation-info-list">
              {Object.entries(breakdown).map(([date, rate]) => (
                <li key={`breakdown-${date}`}>
                  <div>
                    <strong>{date}</strong>
                  </div>
                  <div>
                    <Currency value={rate} />
                  </div>
                </li>
              ))}
              {breakdownLength === 0 && (
                <li className="text-secondary text-center" key="no-data">
                  <small>{ts('rate_breakdown_is_not_available_for_this_reservation')}</small>
                </li>
              )}
            </ul>
            {additionalFees.length > 0 && (
              <>
                <h4>{ts('additional_fees')}</h4>
                <hr />
                <ul className="list-unstyled reservation-info-list">
                  {additionalFees.map((fee) => (
                    <li key={`fee-${fee.key}`}>
                      <div>
                        <strong>{ts(fee.key as TranslationKey)}</strong>
                        {fee.date && (
                          <small className="text-secondary">
                            <DateT
                              options={{ day: '2-digit', month: 'long', year: 'numeric' }}
                              value={fee.date}
                            />
                          </small>
                        )}
                      </div>
                      <div>
                        <Currency value={fee.amount} />
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {rateTotal > 0 && (
              <>
                <hr />
                <Row className="mt-3">
                  <Col xs={6}>
                    <strong>{ts('room_total')}</strong>
                  </Col>
                  <Col className="text-end" xs={6}>
                    <strong>
                      <Currency value={rateTotal} />
                    </strong>
                  </Col>
                </Row>
              </>
            )}
          </Col>
          {rateTotal > 0 && (
            <Col className="mt-3" xs={12}>
              <Alert color="info" fade={false}>
                {ts(
                  'hotel.the_room_total_includes_the_per_night_rate_and_applicable_taxes_and_fees_for_a_full_breakdown_including_taxes_and_fees_refer_to_your_confirmation_email',
                )}
              </Alert>
            </Col>
          )}
        </Row>
      </CardBody>
    </Card>
  );
};

const HotelGuestList: FC<ReservationInfoProps> = ({ reservation }) => {
  // Sort by isPrimaryGuest boolean flag
  const { ts } = useTranslation();

  const guests = (reservation.guests ?? []).sort((gA, gB) => {
    if (gA.isPrimaryGuest) {
      return -1;
    }

    if (gB.isPrimaryGuest) {
      return 1;
    }

    if (!gA.lastName || !gB.lastName) {
      return gA.id < gB.id ? -1 : 1;
    }

    // Otherwise, sort by last name if isPrimaryGuest flag is not set
    return gA.lastName.localeCompare(gB.lastName);
  });

  return (
    <Card>
      <CardBody>
        <h4>{ts('guests')}</h4>
        <hr />
        {guests.map((guest, idx) => {
          const guestNo = idx + 1;

          return (
            <Card className="mb-3" key={guest.id}>
              <CardBody>
                <ElementHeader
                  hideDivider
                  title={
                    <>
                      {guest.firstName} {guest.lastName}{' '}
                      <small>
                        ({ts('guest')} {guestNo})
                      </small>
                    </>
                  }
                >
                  {guest.isPrimaryGuest ? (
                    <Badge color="primary">{ts('primary_guest')}</Badge>
                  ) : (
                    <Badge color="secondary">{ts('additional_guest')}</Badge>
                  )}
                </ElementHeader>
              </CardBody>
            </Card>
          );
        })}
      </CardBody>
    </Card>
  );
};

const HotelPolicyAgreements: FC<ReservationInfoProps> = ({ reservation }) => {
  const { extendedAttributes } = reservation;
  const hotelAgreements: HotelAgreements =
    extendedAttributes?.agreements ?? ({} as HotelAgreements);

  const agreementCount = Object.keys(hotelAgreements).length;

  const { ts } = useTranslation();

  const agreements: React.JSX.Element[] = Object.entries(hotelAgreements)
    .map(([key, value]) => {
      if (!value || value.trim().length === 0) {
        return null;
      }

      const policy = key as keyof HotelAgreements;
      const title = ts(`hotel.policy.${policy}`);

      return (
        <CollapseInfo key={key} title={title}>
          {value}
        </CollapseInfo>
      );
    })
    .filter((a) => !!a);

  return (
    <Card>
      <CardBody>
        <Row>
          <Col lg={agreementCount > 0 ? 6 : 12} xs={12}>
            <h4>{ts('event_hotel_reservation_policy')}</h4>
            <hr />
            <TermsLoader name="hotelReservationTerms" />
          </Col>
          {agreementCount > 0 && (
            <>
              <Col className="d-xl-none" xs={12}>
                <hr />
              </Col>
              <Col lg={6} xs={12}>
                <h4>{ts('reservation_agreements')}</h4>
                <hr />
                <p className="text-secondary">
                  <small>
                    {ts(
                      'hotel.these_are_the_agreements_that_were_provided_with_your_hotel_reservation_by_the_hotel_or_booking_partner_click_on_each_agreement_to_view_more_details',
                    )}
                  </small>
                </p>
                <CollapseContainer>{agreements}</CollapseContainer>
              </Col>
            </>
          )}
        </Row>
      </CardBody>
    </Card>
  );
};
